import { FC } from 'react'

import { Box, Card, Grid, Tag, Title } from '@factorialco/deprecated-design-system'

import { AtsIntegrationsOtherAddonsFeaturesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  atsOtherAddons,
  isAtsSuscriptionAddon,
  isAtsSuscriptionAddonEnabled,
} from 'modules/ats/lib/utils/integrations'
import { AtsAppTypeIds } from 'modules/ats/types/integrations'
import { TYPES } from 'modules/billing/lib/types'
import subscriptionAddons from 'modules/billing/store/collections/subscriptionAddons'
import type SubscriptionAddon from 'modules/billing/store/models/SubscriptionAddon'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'

const ADDON_ORDER: Record<string, number> = {
  [AtsAppTypeIds.AtsWhatsapp]: 0,
  [AtsAppTypeIds.LinkedinLimitedListings]: 1,
  [AtsAppTypeIds.AtsIdealDiscIntegration]: 2,
}

const getAddonOrder = (addonId: string) => ADDON_ORDER[addonId] ?? Number.POSITIVE_INFINITY

export const OtherAddons: FC = () => {
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const ADDON_TO_DATO_ID: Record<string, string> = {
    [AtsAppTypeIds.AtsWhatsapp]: integrationCodes.whatsapp,
    [AtsAppTypeIds.AtsIdealDiscIntegration]: integrationCodes.idealdisc,
  }

  const { data } = useQuery(AtsIntegrationsOtherAddonsFeaturesDocument, {
    variables: {
      companyId: company.id,
    },
  })
  const integrations = subscriptionAddons.models.filter((model) => model.type === TYPES.integration)
  const apps = subscriptionAddons.models.filter((model) =>
    [TYPES.app, TYPES.app_integration].includes(model.type)
  )
  const hasLinkedinLimitedListings =
    data?.features.featuresConnection.nodes.some((feature) => feature.enabled) ?? false
  const atsIntegrations: SubscriptionAddon[] = [...integrations, ...apps]
    .filter(
      (addon) =>
        isAtsSuscriptionAddon(addon.id) && isAtsSuscriptionAddonEnabled(atsOtherAddons()[addon.id])
    )
    .sort((first, second) => {
      const firstIndex = getAddonOrder(first.id)
      const secondIndex = getAddonOrder(second.id)
      if (firstIndex === secondIndex) return 0
      return firstIndex - secondIndex
    })

  if (!atsIntegrations.length && !hasLinkedinLimitedListings) {
    return null
  }

  const buildAddonCard = (addonId: string, enabled: boolean): JSX.Element | null => {
    const integrationData = atsOtherAddons()[addonId]
    if (!integrationData) return null

    const datoId = ADDON_TO_DATO_ID[addonId]
    const marketplaceLink = datoId
      ? navigation.discover.marketplace.item.fullPath({ id: datoId })
      : navigation.discover.marketplace.fullPath()

    return (
      <Card key={addonId} img={integrationData.image.src} to={marketplaceLink}>
        {enabled && (
          <Card.Eyebrow>
            <Tag type="neutral">{i18n.t('ats.settings.integrations.other.configured')}</Tag>
          </Card.Eyebrow>
        )}
        <Card.Title>{integrationData.title}</Card.Title>
        <Card.Description>{integrationData.description}</Card.Description>
      </Card>
    )
  }

  const addonCards = atsIntegrations
    .map((addon) => buildAddonCard(addon.id, Boolean(addon.attributes.enabled)))
    .filter((card): card is JSX.Element => Boolean(card))

  const hasLinkedinCard = atsIntegrations.some(
    (addon) => addon.id === AtsAppTypeIds.LinkedinLimitedListings
  )
  if (hasLinkedinLimitedListings && !hasLinkedinCard) {
    const linkedinCard = buildAddonCard(AtsAppTypeIds.LinkedinLimitedListings, true)
    if (linkedinCard) {
      const idealdiscIndex = addonCards.findIndex(
        (card) => card?.key === AtsAppTypeIds.AtsIdealDiscIntegration
      )
      if (idealdiscIndex === -1) {
        addonCards.push(linkedinCard)
      } else {
        addonCards.splice(idealdiscIndex, 0, linkedinCard)
      }
    }
  }

  return (
    <Box paddingX="s64">
      <Title
        variant="500"
        title={i18n.t('ats.settings.integrations.other.title')}
        description={i18n.t('ats.settings.integrations.other.description')}
      />
      <Grid variant="medium">{addonCards}</Grid>
    </Box>
  )
}
